<template>
  <div class="container">
    <form @submit.prevent="updateFile" class="form-container">
      <div class="form-group">
        <span class="url">url: ../{{getURL() }}</span>
        <div class="input-container">
          <span class="display-name"><b>Display name: </b></span>
          <input
            type="text"
            class="form-control"
            v-model="alias"
            @input="restrictInput($event)"
            required
          >
        </div>
        <ListTag
          :topic="topicKey"
          :initialTags="getFileTags()"
          :selectCallback="onTagsUpdate"
          class="list-tag"
        ></ListTag>
        <Message
          class="info-message"
          :message="message.message"
          :level="message.level"
        ></Message>
      </div>
      <button
        type="submit"
        class="btn btn-primary"
        :disabled="!isSubmitPossible"
      >
        Update File
      </button>
    </form>
  </div>
</template>

<script>

import projectconfig from '@/project-config';

import dynamoDB from '@/aws/aws-dynamoDB';

import Message from '@/components/message.vue';
import ListTag from '@/components/tag-list.vue';

import arrayEqual from '@/utils/arrayEqual';
import removeEmpty from '@/utils/objectRemoveEmpty';

export default {
  name: 'EditFile',
  components: {
    Message,
    ListTag,
  },
  props: [
    'file',
    'topicKey',
    'successCallback',
  ],
  data() {
    return {
      alias: '',
      key: '',
      error: '',
      selectedTags: this.getFileTags(),
      message: {
        message: '',
        level: 'info',
      },
    };
  },
  computed: {
    // submit is possible if the alias or the tags have changed
    isSubmitPossible() {
      if (this.file.alias !== this.alias) {
        return true;
      }
      if (!arrayEqual(this.selectedTags, this.getFileTags())) {
        return true;
      }

      return false;
    },
  },
  watch: {
    selectedTags() {
      // Clear eventual message when user make update
      this.message = {
        level: 'info',
        message: '',
      };
    },
  },
  async created() {
    // So the original file name is not changed
    this.alias = this.file.alias;
  },
  methods: {
    /**
     * Update file name (alias)
     */
    async updateFile() {
      try {
        let params = {
          id: this.file.key,
        };

        if (this.file.alias !== this.alias) {
          params.alias = this.alias;
        }
        if (!arrayEqual(this.selectedTags, this.getFileTags())) {
          params.metadata = this.file.metadata;
          params.metadata.tags = this.selectedTags;
        }
        params = removeEmpty(params);

        const result = await dynamoDB.updateDBFile(params);

        if (result.$metadata.httpStatusCode >= 200 && result.$metadata.httpStatusCode < 300) {
          this.successCallback();
        } else {
          this.message = {
            level: 'error',
            message: result,
          };
        }
      } catch (e) {
        this.message = {
          level: 'error',
          message: e.message,
        };
      }
    },
    restrictInput(event) {
      const alias = event.target.value;
      if (alias.length > 45) {
        this.alias = alias.slice(0, 45);
        this.message = {
          message: 'Maximum characters reached (45)',
          level: 'warning',
        };
      } else {
        this.message.message = '';
      }
    },
    getURL() {
      if (projectconfig.URL_FORMATTING.MODE === 'RELATIVE') {
        return this.file.dynamicURL;
      }
      return this.file.Key;
    },
    getFileTags() {
      if (this.file.metadata && this.file.metadata.tags) {
        return this.file.metadata.tags.map(tag => tag.id);
      }
      return [];
    },
    onTagsUpdate(tags) {
      this.selectedTags = removeEmpty(tags);
    },
  },
};
</script>
<style lang="stylus" scoped>
  @require '../style/colors'

  .container
    display: flex
    flex-flow: column nowrap
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)
    border-radius: 8px
    margin: 10px
    padding: 20px
    width: 650px

  .form-container
    display: flex
    flex-flow: column nowrap
    align-items: center
    gap: 15px

  .input-container
    display: flex
    flex-flow: row nowrap
    align-items: center
    gap: 15px

  .form-group
    display: flex
    flex-flow: column nowrap
    gap: 15px
    margin-bottom: 20px
    width: 100%

  .info-message
    align-self: center

  .url
    font-size: 15px
    color: $black60

  .display-name
    display: block
    font-size: 18px
    margin-bottom: 5px
    white-space: nowrap

  input[type="text"]
    display: block
    padding: 10px
    font-size: 16px
    border: none
    background-color: linear-gradient(to right, $primary-color, $secondary-color)
    border-radius: 4px
    box-shadow: inset 1px 1px 9px rgba(0, 0, 0, 0.1)
    color: #1a1a1a
    width: 100%

  button[type="submit"]
    background-color: $primary-color
    color: $white
    font-size: 1.7rem
    padding: 1.3rem 2.5rem
    border: none
    border-radius: .5rem
    cursor: pointer
    max-width: 20rem
    transition: background-color 0.3s ease-in-out

    &:hover
      background-color: darken($primary-color, 10%)

    &:disabled
      /* Disabled state styles */
      background-color: $secondary-color;
      cursor: not-allowed;

</style>
